import { getAdminToken } from '../api'

export type DocWritingAction = 'chat' | 'outline' | 'continue' | 'polish' | 'expand' | 'ask'

export type DocWritingFormat = 'markdown' | 'html'

/** 错误来源：便于区分第三方模型、服务端与前端 */
export type DocWritingErrorSource =
  | 'config'
  | 'llm_api'
  | 'llm_empty'
  | 'internal'
  | 'frontend'
  | 'http'

export type DocWritingFailure = {
  source: DocWritingErrorSource
  message: string
  detail?: string
}

export type DocWritingStreamEvent =
  | { type: 'status'; phase: string; message?: string }
  | { type: 'content'; text: string }
  | { type: 'done'; content?: string }
  | { type: 'error'; source?: DocWritingErrorSource; message: string; detail?: string }

export const WRITING_ERROR_SOURCE_LABEL: Record<DocWritingErrorSource, string> = {
  config: '配置',
  llm_api: '第三方模型',
  llm_empty: '第三方模型（空正文）',
  internal: '服务端',
  frontend: '前端流式',
  http: 'HTTP 请求',
}

export class DocWritingStreamError extends Error {
  source: DocWritingErrorSource
  detail?: string

  constructor(source: DocWritingErrorSource, message: string, detail?: string) {
    super(message)
    this.name = 'DocWritingStreamError'
    this.source = source
    this.detail = detail
  }
}

export function failureFromErrorEvent(ev: Extract<DocWritingStreamEvent, { type: 'error' }>): DocWritingFailure {
  return {
    source: ev.source ?? 'internal',
    message: ev.message,
    detail: ev.detail,
  }
}

export function formatWritingFailureBubble(failure: DocWritingFailure): string {
  let text = failure.message
  if (failure.detail?.trim()) {
    text += `\n\n技术信息：${failure.detail.trim()}`
  }
  return text
}

export function failureFromThrownError(e: unknown): DocWritingFailure {
  if (e instanceof DocWritingStreamError) {
    return { source: e.source, message: e.message, detail: e.detail }
  }
  const msg = e instanceof Error ? e.message : String(e)
  if (/fetch|network|Failed to fetch/i.test(msg)) {
    return {
      source: 'http',
      message: '无法连接写作接口，请确认后端已启动且网络正常。',
      detail: msg,
    }
  }
  return {
    source: 'frontend',
    message: '前端处理流式响应时出错。',
    detail: msg,
  }
}

export type DocWritingMessage = {
  role: 'user' | 'assistant'
  content: string
}

export type DocWritingStreamBody = {
  format: DocWritingFormat
  action: DocWritingAction
  user_message: string
  title?: string
  category?: string
  selection?: string
  document_excerpt?: string
  messages?: DocWritingMessage[]
}

export async function streamDocWriting(
  body: DocWritingStreamBody,
  onEvent: (ev: DocWritingStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = getAdminToken()
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch('/api/admin/knowledge/doc-writing/stream', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok) {
    let msg = res.statusText
    try {
      const j = (await res.json()) as { detail?: string }
      if (typeof j.detail === 'string') msg = j.detail
    } catch {
      /* ignore */
    }
    throw new DocWritingStreamError(
      'http',
      `写作接口 HTTP ${res.status}：${msg || '请求失败'}`,
      `status=${res.status}; url=/api/admin/knowledge/doc-writing/stream`,
    )
  }

  const reader = res.body?.getReader()
  if (!reader) {
    throw new DocWritingStreamError('frontend', '浏览器不支持流式响应（response.body 为空）')
  }

  const decoder = new TextDecoder()
  let buf = ''

  const flushBlocks = (blocks: string[]) => {
    for (const block of blocks) {
      const line = block.trim()
      if (!line.startsWith('data:')) continue
      const raw = line.slice(5).trim()
      if (!raw) continue
      try {
        onEvent(JSON.parse(raw) as DocWritingStreamEvent)
      } catch {
        /* skip malformed chunk; 流结束时若仍无 done/content 由调用方报前端错误 */
      }
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    const parts = buf.split('\n\n')
    buf = parts.pop() ?? ''
    flushBlocks(parts)
  }
  if (buf.trim()) {
    flushBlocks([buf])
  }
}

/** 去掉模型可能包裹的 ```markdown / ```html _fence */
export function stripWritingFence(text: string, format: DocWritingFormat): string {
  let s = text.trim()
  const fence = format === 'markdown' ? 'markdown' : 'html'
  const re = new RegExp(`^\`\`\`(?:${fence})?\\s*\\n?([\\s\\S]*?)\\n?\`\`\`$`, 'i')
  const m = s.match(re)
  if (m) s = m[1].trim()
  return s
}

/** 侧栏对话展示用：HTML 转成可读纯文本，插入编辑器仍用原始 HTML */
export function writingContentForDisplay(content: string, format: DocWritingFormat): string {
  const raw = content.trim()
  if (!raw || format !== 'html') return raw
  let s = raw
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|h[1-6]|li|tr|table|ul|ol)\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
  return s.replace(/\n{3,}/g, '\n\n').trim()
}
